import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular-boost';
import { Observable } from 'rxjs';
import { catchError, filter, map, startWith, take } from 'rxjs/operators';
import { candidateTagsQuery } from '../graphql/queries/candidateTags';
import {
  DriveType,
  IAccount,
  IAudit,
  IGroup,
  IFunnel,
  IFunnelLink,
  ICompany,
  ICustomAttribute,
  IDrive,
  IInbox,
  ILabel,
  InboxType,
  IProfile,
  ICandidate,
  ISpreadsheet,
  ISubscription,
  ITag,
  IReview,
  ITimeRange,
  ITimeTable,
  ISchedule,
  ILogon,
  ILogonData,
} from '../interfaces/graph-types';

type SuccessCallback = (data: any) => any;
type ErrorCallback = (err: string) => any;

@Injectable()
export class AccountService {
  // ------------------------------------------------------
  constructor(private apollo: Apollo) {}

  // ------------------------------------------------------
  public queryGraphQL(query, variables): Observable<any> {
    return this.apollo.watchQuery<any>({
      variables: variables,
      query: query,
      // About fetch policies: https://www.apollographql.com/docs/react/api/react-apollo.html#graphql-config-options-fetchPolicy
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: false,
    }).valueChanges;
  }

  // ------------------------------------------------------
  public queryGraphQLPoll(query, variables, pollInterval): Observable<any> {
    return this.apollo.watchQuery<any>({
      variables: variables,
      query: query,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: false,
      pollInterval: pollInterval,
    }).valueChanges;
  }

  // ------------------------------------------------------
  public queryGraphQLNoCache(query, variables): Observable<any> {
    return this.apollo.watchQuery<any>({
      variables: variables,
      query: query,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: false,
    }).valueChanges;
  }

  // ------------------------------------------------------
  public stats(accountId: string, selected: string = null) {
    return this.apollo
      .watchQuery<{ account: IAccount }>({
        variables: { accountId, selected },
        query: gql`
          query accountStats($accountId: ID!, $selected: ID) {
            account(accountId: $accountId) {
              id
              volumeStats(selected: $selected)
              inboxes {
                id
                name
                accredited
                paused
                email
                type
                sharing
                canView
                sendQueueSize
                dailySendingInfo {
                  quota
                  sendCount
                }
              }
            }
          }
        `,
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
      })
      .valueChanges.pipe(map(res => res.data.account));
  }

  // ------------------------------------------------------
  public inboxes(
    accountId: string,
    inboxType: InboxType = null,
  ): Observable<IInbox[]> {
    let variables = { accountId, inboxType };

    let query = gql`
      query inboxes($accountId: ID!, $inboxType: String) {
        account(accountId: $accountId) {
          id
          inboxes(inboxType: $inboxType) {
            id
            name
            email
            accredited
            avatarUrl
            type
            sendQueueSize
            queueLockedFor
            sharing
            canView
            paused
            pendingApplications {
              id
              candidate {
                id
                name
                email
                title
                role
                avatarUrl
              }
            }
            dailySendingInfo {
              quota
              sendCount
            }
          }
        }
      }
    `;

    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.account.inboxes),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public drives(
    accountId: string,
    driveType: DriveType = null,
    scope: string = 'all',
  ): Observable<IDrive[]> {
    let variables = { accountId, driveType, scope };

    let query = gql`
      query drives($accountId: ID!, $driveType: String, $scope: String) {
        account(accountId: $accountId) {
          id
          drives(driveType: $driveType, scope: $scope) {
            id
            name
            email
            accredited
            avatarUrl
            type
            sharing
            canView
          }
        }
      }
    `;

    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.account.drives),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public autoImports(
    accountId: string,
    driveType: DriveType = null,
    scope: string = 'all',
  ): Observable<IDrive[]> {
    let variables = { accountId, driveType, scope };

    let query = gql`
      query autoImports($accountId: ID!) {
        account(accountId: $accountId) {
          id
          autoImports {
            id
            name
            drive {
              id
              name
              email
            }
            logon {
              id
              name
              email
            }
            funnel {
              id
              name
            }
            group {
              id
              name
            }
            paused
            lastError
            checkedAt
          }
        }
      }
    `;

    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.account.autoImports),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public spreadsheets(
    driveId: string,
    searchText: string,
  ): Observable<ISpreadsheet[]> {
    return this.apollo
      .watchQuery<{ drive: IDrive }>({
        variables: { driveId, searchText },
        query: gql`
          query spreadsheet($driveId: ID!, $searchText: String) {
            drive(driveId: $driveId) {
              id
              spreadsheets(searchText: $searchText) {
                id
                name
                url
                changedLast
                owner
              }
            }
          }
        `,
        fetchPolicy: 'no-cache',
        notifyOnNetworkStatusChange: false,
      })
      .valueChanges.pipe(take(1))
      .pipe(
        map(({ data }) => data.drive.spreadsheets),
        startWith(null),
      );
  }

  // ------------------------------------------------------
  public fetchSpreadsheetColumns(
    driveId: string,
    accountId: string,
    sheetId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      driveId,
      accountId,
      sheetId,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation fetchSpreadsheetColumns(
            $input: FetchSpreadsheetColumnsInput!
          ) {
            fetchSpreadsheetColumns(input: $input) {
              handshakeId
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        console.log(result);
        let data = result['data'].fetchSpreadsheetColumns;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          console.log('Success');
          console.log(data);
          if (successfunction) {
            successfunction(data);
          }
          return null;
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public getAccountInfo(accountId: string): Observable<IAccount> {
    let variables = { accountId };

    let query = gql`
      query getAccountInfo($accountId: ID!) {
        account(accountId: $accountId) {
          id
          name
          customDomainTracking
          trackingDelayInSec
          emailReportDeliveryTest
          bcc
          timeZone
          openNotification
          openSound
          clickNotification
          clickSound
          colleaguesShareRole
          plan
          organization {
            id
            name
            hidden
            orgtype
          }
          subscription {
            id
            name
            cycle
            quantity
            status
          }
        }
      }
    `;

    return this.queryGraphQLNoCache(query, variables).pipe(
      filter(response => response.data),
      map(({ data }) => data.account),
    );
  }

  // ------------------------------------------------------
  public getOrganizationId(accountId: string): Observable<IAccount> {
    let variables = { accountId };

    let query = gql`
      query getOrgId($accountId: ID!) {
        account(accountId: $accountId) {
          id
          organization {
            id
          }
        }
      }
    `;

    return this.queryGraphQL(query, variables).pipe(
      filter(response => response.data),
      map(({ data }) => data.account),
    );
  }

  // ------------------------------------------------------
  public getStarted(accountId: string): Observable<IAccount> {
    let variables = { accountId };

    let query = gql`
      query getStarted($accountId: ID!) {
        account(accountId: $accountId) {
          id
          hasInboxes
          hasCandidates
          hasFunnels
          hasApplications
        }
      }
    `;

    return this.queryGraphQL(query, variables).pipe(
      filter(response => response.data),
      map(({ data }) => data.account),
    );
  }

  // ------------------------------------------------------
  public billing(accountId: string): Observable<IAccount> {
    let variables = { accountId };

    let query = gql`
      query billing($accountId: ID!) {
        account(accountId: $accountId) {
          id
          organization {
            id
            hidden
            orgtype
            billing {
              id
              ccCountry
              ccBrand
              ccExpMonth
              ccExpYear
              ccName
              ccLast4
              discountPercent
            }
          }
        }
      }
    `;

    return this.queryGraphQL(query, variables).pipe(
      filter(response => response.data),
      map(({ data }) => data.account),
    );
  }

  // ------------------------------------------------------
  public schedules(accountId: string): Observable<ISchedule[]> {
    let variables = { accountId };

    let query = gql`
      query schedules($accountId: ID!) {
        account(accountId: $accountId) {
          id
          schedules {
            id
            funnel {
              id
              name
            }
            triggerAt
            checkAt
            candidateCount
            group {
              id
              name
              owner {
                id
                name
              }
            }
          }
        }
      }
    `;

    return this.queryGraphQL(query, variables).pipe(
      filter(response => response.data),
      map(({ data }) => data.account.schedules),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public initiateSetupIntent(
    accountId: string,
    name: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ): null {
    const input = {
      accountId,
      name,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation initiateSetupIntent($input: InitiateSetupIntentInput!) {
            initiateSetupIntent(input: $input) {
              secret
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('ERROR');
          errorfunction(err);
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].initiateSetupIntent;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public completeSetupIntent(
    accountId: string,
    paymentMethod: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ): null {
    const input = {
      accountId,
      paymentMethod,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation completeSetupIntent($input: CompleteSetupIntentInput!) {
            completeSetupIntent(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('ERROR');
          errorfunction(err);
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].completeSetupIntent;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public getCharts(accountId: string): Observable<IAccount> {
    let variables = { accountId };

    let query = gql`
      query getAccountCharts($accountId: ID!) {
        account(accountId: $accountId) {
          id
          name
        }
      }
    `;

    return this.queryGraphQL(query, variables).pipe(
      filter(response => response.data),
      map(({ data }) => data.account),
    );
  }

  // ------------------------------------------------------
  public actions(accountId: string): Observable<IAccount> {
    let variables = { accountId };

    let query = gql`
      query actions($accountId: ID!) {
        account(accountId: $accountId) {
          id
          totalReviews
        }
      }
    `;

    return this.queryGraphQLPoll(query, variables, 600000).pipe(
      filter(response => response.data),
      map(({ data }) => data.account),
    );
  }

  // ------------------------------------------------------
  public notificationCounters(accountId: string): Observable<IAccount> {
    let variables = { accountId };

    let query = gql`
      query notificationCounters($accountId: ID!) {
        account(accountId: $accountId) {
          id
          totalReviews
        }
      }
    `;

    return this.queryGraphQLPoll(query, variables, 600000).pipe(
      filter(response => response.data),
      map(({ data }) => data.account),
    );
  }

  // ------------------------------------------------------
  public getAircallConfig(accountId: string): Observable<IAccount> {
    let variables = { accountId };

    let query = gql`
      query getAircallConfig($accountId: ID!) {
        account(accountId: $accountId) {
          id
          teamMembers {
            id
            name
            email
            avatarUrl
            aircallUserId(accountId: $accountId)
            aircallNumberId(accountId: $accountId)
          }
          aircallUsers {
            id
            name
            email
          }
          aircallNumbers {
            id
            name
            digits
            country
          }
        }
      }
    `;

    return this.queryGraphQL(query, variables).pipe(
      filter(response => response.data),
      map(({ data }) => data.account),
    );
  }

  // ------------------------------------------------------
  public getAddonsInfo(accountId: string): Observable<IAccount> {
    let variables = { accountId };

    let query = gql`
      query getAddonsInfo($accountId: ID!) {
        account(accountId: $accountId) {
          id
          emailVerificationCredits
          briteverifyApi
          dropcontactApi
          hunterApi
          neverbounceApi
          zerobounceApi
          rejectRiskyEmails
          verifyEmailBeforeSending
          emailVerificationValidityInDays
          zapierApiKey
          twilioAccountSid
          twilioAuthToken
          twilioPhoneNumber
          webhookEnabled
          webhookOpenFirst
          webhookOpenRepeat
          webhookClickFirst
          webhookClickRepeat
          webhookReplyFirst
          webhookReplyRepeat
          webhookBounce
          webhookUnsubscribe
          webhookTag
          webhookTaskCompleted
          webhookApplicationCompleted
          webhookApplicationCheckpoint
          phoneIntegration
          zapierTriggers {
            id
            eventName
            logon {
              id
              name
              email
            }
          }
          organization {
            id
            orgtype
          }
        }
      }
    `;

    return this.queryGraphQL(query, variables).pipe(
      filter(response => response.data),
      map(({ data }) => data.account),
    );
  }

  // ------------------------------------------------------
  public getAddonsCredits(accountId: string): Observable<IAccount> {
    let variables = { accountId };

    let query = gql`
      query getAddonsCredits($accountId: ID!) {
        account(accountId: $accountId) {
          id
          dropcontactCredits {
            error
            value
          }
          hunterCredits {
            error
            value
          }
          neverbounceCredits {
            error
            value
          }
          zerobounceCredits {
            error
            value
          }
        }
      }
    `;

    return this.queryGraphQL(query, variables).pipe(
      filter(response => response.data),
      map(({ data }) => data.account),
    );
  }

  // ------------------------------------------------------
  public getInboxURLs(accountId: string): Observable<IAccount> {
    let variables = { accountId };

    let query = gql`
      query getInboxURLs($accountId: ID!) {
        account(accountId: $accountId) {
          id
          urlInboxGoogle
          urlInboxMicrosoft
        }
      }
    `;

    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => {
        return data.account;
      }),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public getDriveURLs(id: string, level: string): Observable<IAccount> {
    if (level == 'account') {
      let variables = { accountId: id, level };
      let query = gql`
        query getInboxURLs($accountId: ID!) {
          account(accountId: $accountId) {
            id
            urlDriveGoogle
          }
        }
      `;
      return this.queryGraphQLNoCache(query, variables).pipe(
        map(({ data }) => {
          return data.account;
        }),
        startWith(null),
      );
    }
    if (level == 'organization') {
      let variables = { orgId: id, level };
      let query = gql`
        query getInboxURLs($orgId: ID!) {
          organization(orgId: $orgId) {
            id
            urlDriveGoogle
          }
        }
      `;
      return this.queryGraphQLNoCache(query, variables).pipe(
        map(({ data }) => {
          return data.organization;
        }),
        startWith(null),
      );
    }

    throw 'Type unknown';
  }

  // ------------------------------------------------------
  public audits(accountId: string, avatarSize: number): Observable<IAudit[]> {
    let variables = { accountId };

    let query = gql`
      query accountChanges($accountId: ID!) {
        account(accountId: $accountId) {
          id
          audits {
            id
            author {
              id
              name
              email
              avatarUrl(size: ${avatarSize})
            }
            name
            createdAt
            completedAt
            error
          }
        }
      }
    `;
    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.account.audits),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public subscription(accountId: string): Observable<ISubscription> {
    let variables = { accountId };

    let query = gql`
      query subscription($accountId: ID!) {
        account(accountId: $accountId) {
          id
          subscription {
            id
            plan
            interval
            inboxes
            costInCents
          }
        }
      }
    `;

    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.account.subscription),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public inbox(accountId: string, inboxId): Observable<IInbox> {
    let variables = { accountId };

    let query = gql`
      query inbox($accountId: ID!) {
        account(accountId: $accountId) {
          id
          inbox(inboxId: ${inboxId}){
            id
            name
            friendlyName
            email
            sharing
            allowAutoWarmer
            type
            accredited
            reGrantUrl
            owner {
              id
              name
              email
            }
            paused
            canUnpause
            queueLockedFor
            cursor
            replyTo
            signature
            bcc
            throttle
            throttleRange
            generateReply
            generateCandidates
            autoTestDeliverability
            smtpAddress
            smtpPort
            smtpLogin
            smtpPassword
            autosync
            imapAddress
            imapPort
            imapLogin
            imapPassword
            lastSentAt
            checkedAt
            defaultDelay
            deliverabilityItems {
              id
              providerType
              description
              folder
              category
              spfError
              spfWarning
              dkimError
              dkimWarning
              createdAt
            }
            addCandidateToFunnel {
              id
              name
            }
            autoWarmerGroups {
              id
              name
              secret
              intensity
              inboxesCount
            }
          }
        }
      }
    `;
    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.account.inbox),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public deleteInbox(
    inboxId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      inboxId,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation deleteInbox($input: DeleteInboxInput!) {
            deleteInbox(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          } else {
            console.log('Error: ');
            console.log(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].deleteInbox;
        if (data.error) {
          if (errorfunction) {
            errorfunction(data.error);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        } else {
          console.log('Success');
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public auditsMutations(accountId: string): Observable<IAccount> {
    let variables = { accountId };
    let query = gql`
      query auditsMutations($accountId: ID!) {
        account(accountId: $accountId) {
          id
          auditsMutationData {
            type
            count
            name
          }
        }
      }
    `;

    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.account),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public auditsLogons(accountId: string): Observable<ILogonData[]> {
    let variables = { accountId };
    let query = gql`
      query auditsLogons($accountId: ID!) {
        account(accountId: $accountId) {
          id
          auditsLogons {
            count
            logon {
              id
              name
              email
            }
          }
        }
      }
    `;

    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.account.auditsLogons),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public teamMembers(accountId: string): Observable<IProfile[]> {
    let variables = { accountId };
    let query = gql`
      query teamMembers($accountId: ID!) {
        account(accountId: $accountId) {
          id
          teamMembers {
            id
            name
            email
          }
        }
      }
    `;

    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.account.teamMembers),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public funnelNames(
    accountId: string,
    archived: boolean,
    logonId: string,
  ): Observable<IFunnel[]> {
    let variables = { accountId, archived, logonId };
    let query = gql`
      query funnels($accountId: ID!, $archived: Boolean, $logonId: ID) {
        account(accountId: $accountId) {
          id
          funnels(archived: $archived, logonId: $logonId) {
            id
            name
          }
        }
      }
    `;

    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.account.funnels),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public funnels(
    accountId: string,
    archived: boolean,
    logonId: string,
  ): Observable<IFunnel[]> {
    let variables = { accountId, archived, logonId };
    let query = gql`
      query funnels($accountId: ID!, $archived: Boolean, $logonId: ID) {
        account(accountId: $accountId) {
          id
          funnels(archived: $archived, logonId: $logonId) {
            id
            name
            description
            live
            applicationCount
            sharing
            createdAt
            cacheOpen
            rateOpen
            cacheClick
            rateClick
            cacheReply
            rateReply
            cacheUnsub
            rateUnsub
            cacheBounce
            rateBounce
            owner {
              id
              name
            }
            inboxes {
              id
              name
              email
            }
            steps {
              id
              variations {
                id
                type
                business
                waitForCompletion
                asDraft
              }
            }
          }
        }
      }
    `;

    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.account.funnels),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public timetables(accountId: string): Observable<ITimeTable[]> {
    let variables = { accountId };
    let query = gql`
      query timetables($accountId: ID!) {
        account(accountId: $accountId) {
          id
          timeTables {
            id
            name
            timeZone
            workingDays
            timeRanges {
              id
              wday
              fromMinute
              toMinute
            }
          }
        }
      }
    `;

    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.account.timeTables),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public funnelsNames(accountId: string): Observable<IFunnel[]> {
    let variables = { accountId };
    let query = gql`
      query funnelsNames($accountId: ID!) {
        account(accountId: $accountId) {
          id
          funnels {
            id
            name
            live
          }
        }
      }
    `;

    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.account.funnels),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public candidateAttributes(
    accountId: string,
  ): Observable<ICustomAttribute[]> {
    let variables = { accountId };
    let query = gql`
      query candidateAttributes($accountId: ID!) {
        account(accountId: $accountId) {
          id
          candidateAttributes {
            id
            name
            icon
            value
          }
        }
      }
    `;
    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.account.candidateAttributes),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public queryCandidateAttributes(accountId: string) {
    const query = gql`
      query candidateAttributes($accountId: ID!) {
        account(accountId: $accountId) {
          id
          candidateAttributes {
            id
            name
            icon
            value
          }
        }
      }
    `;

    return this.apollo.query<ICustomAttribute[]>({
      query,
      variables: { accountId },
    });
  }

  // ------------------------------------------------------
  public groups(accountId: string): Observable<IGroup[]> {
    let variables = { accountId };

    let query = gql`
      query groups($accountId: ID!) {
        account(accountId: $accountId) {
          id
          groups {
            id
            name
            icon
            sharing
            rejectPreviouslyContacted
            verifyEmails
            rejectRiskyEmails
            rejectUnknownEmails
            rejectInvalidEmails
            candidatesCount
            emailVerificationProvider {
              id
              name
            }
            createdAt
            owner {
              id
              name
            }
          }
        }
      }
    `;

    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.account.groups),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public verificationTools(accountId: string): Observable<IAccount> {
    let variables = { accountId };

    let query = gql`
      query verificationTools($accountId: ID!) {
        account(accountId: $accountId) {
          id
          verificationTools {
            id
            name
          }
        }
      }
    `;

    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.account),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public tags(accountId: string): Observable<ITag[]> {
    let variables = { accountId };

    let query = gql`
      query tags($accountId: ID!) {
        account(accountId: $accountId) {
          id
          candidateTags {
            id
            name
            icon
          }
        }
      }
    `;

    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.account.candidateTags),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public candidateTags(accountId: string): Observable<ITag[]> {
    return this.apollo
      .watchQuery<any>({
        variables: { accountId },
        query: candidateTagsQuery,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
      })
      .valueChanges.pipe(
        map(({ data }) => data.account.candidateTags),
        startWith(null),
      );
  }

  // ------------------------------------------------------
  public queryCandidateTags(accountId: string) {
    const query = gql`
      query candidateTags($accountId: ID!) {
        account(accountId: $accountId) {
          id
          candidateAttributes {
            id
            name
            icon
            value
          }
        }
      }
    `;

    return this.apollo.query<ITag>({
      query,
      variables: { accountId },
    });
  }

  // ------------------------------------------------------
  public reviews(accountId: string): Observable<IReview[]> {
    let variables = { accountId };

    let query = gql`
      query reviews($accountId: ID!) {
        account(accountId: $accountId) {
          id
          reviews {
            id
            body
            bodyExtract
            candidate {
              id
              name
              email
              title
              role
              avatarUrl
            }
          }
        }
      }
    `;

    return this.queryGraphQLNoCache(query, variables).pipe(
      map(({ data }) => data.account.reviews),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public funnelLinks(accountId): Observable<IFunnelLink[]> {
    let variables = { accountId };
    let query = gql`
      query funnelLinks($accountId: ID!) {
        account(accountId: $accountId) {
          id
          organization {
            id
            funnelLinks {
              id
              public
              createdAt
              public
              owner {
                id
                email
                name
              }
              funnel {
                id
                name
                description
                stepCount
              }
            }
          }
        }
      }
    `;
    return this.queryGraphQL(query, variables).pipe(
      filter(response => response.data),
      map(({ data }) => data.account.organization.funnelLinks),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public funnel(funnelId): Observable<IFunnel> {
    let variables = { funnelId };
    let query = gql`
      query funnel($funnelId: ID!) {
        funnel(funnelId: $funnelId) {
          id
          name
          description
          live
          archived
          sharing
          steps {
            id
            position
            type
            variations {
              id
              disabled
              type
              statement
              descriptionHtml
              descriptionExtract
              acceptBox
              required
              choices
              minSelection
              maxSelection
              randomized
              minScore
              manualReview
              explanation
            }
          }
        }
      }
    `;
    return this.queryGraphQL(query, variables).pipe(
      filter(response => response.data),
      map(({ data }) => data.funnel),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public resolveFunnel(funnelId, candidateId, inboxId): Observable<IFunnel> {
    let variables = { funnelId, candidateId, inboxId };
    let query = gql`
      query resolveFunnel($funnelId: ID!, $candidateId: ID, $inboxId: ID) {
        funnel(
          funnelId: $funnelId
          candidateId: $candidateId
          inboxId: $inboxId
        ) {
          id
          name
          live
          archived
          sharing
          steps {
            id
            position
            variations {
              id
              type
              subject
              body
              bodyExtract
              continueThread
              openTracking
              clickTracking
              asDraft
              cced
              unit
              value
              business
              name
              waitForCompletion
              assignee
              hookUrl
              title
              text
              message
              textMessage
              description
              targetUrl
              request
              params
              cookies
              payload
              conditionTags {
                id
                have
                tag {
                  id
                  name
                }
              }
            }
          }
        }
      }
    `;
    return this.queryGraphQLNoCache(query, variables).pipe(
      filter(response => response.data),
      map(({ data }) => data.funnel),
      startWith(null),
    );
  }

  // ------------------------------------------------------
  public searchAccountCandidates(
    accountId: string,
    search: string,
    excludeCompanyId: string = null,
  ): Observable<ICandidate[]> {
    return (
      this.apollo
        .watchQuery<{ account: IAccount }>({
          variables: {
            accountId: accountId,
            excludeCompanyId: excludeCompanyId,
          },
          query: gql`
          query searchAccountCandidates($accountId: ID!, $excludeCompanyId: String) {
            account(accountId: $accountId) {
              id
              candidates(first: 5, search: "${search}", excludeCompanyId: $excludeCompanyId) {
                edges {
                  node {
                    id
                    name
                    email
                    avatarUrl
                  }
                }
              }
            }
          }
        `,
          fetchPolicy: 'network-only', // no-cache has a bug with apollo, it gets called twice and causes some problems with front end (re-open the menu)
          notifyOnNetworkStatusChange: false,
        })
        // .
        .valueChanges //.pipe(distinctUntilChanged())
        .pipe(map(res => res.data.account.candidates.edges.map(e => e.node)))
    );
  }

  // ------------------------------------------------------
  public initiatePaymentIntent(
    accountId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ): null {
    const input = {
      accountId,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation initiatePaymentIntent($input: InitiatePaymentIntentInput!) {
            initiatePaymentIntent(input: $input) {
              clientSecret
              paymentMethod
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('ERROR');
          errorfunction(err);
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].initiatePaymentIntent;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public refreshSubscription(
    accountId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId: accountId,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation refreshSubscription($input: RefreshSubscriptionInput!) {
            refreshSubscription(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('ERROR');
          errorfunction(err);
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].refreshSubscription;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public purchaseSubscription(
    accountId: string,
    plan: string,
    inboxCount: number,
    billingInterval: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId: accountId,
      plan: plan,
      inboxCount: inboxCount,
      billingInterval: billingInterval,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation purchaseSubscription($input: PurchaseSubscriptionInput!) {
            purchaseSubscription(input: $input) {
              stripeConfirmationSecret
              stripePaymentMethod
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('ERROR');
          errorfunction(err);
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].purchaseSubscription;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public cancelSubscription(
    accountId: string,
    reason: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      reason,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation cancelSubscription($input: CancelSubscriptionInput!) {
            cancelSubscription(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].cancelSubscription;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public generateZapierApiKey(
    accountId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation generateZapierApiKey($input: GenerateZapierApiKeyInput!) {
            generateZapierApiKey(input: $input) {
              zapierApiKey
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].generateZapierApiKey;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public createCandidate(
    accountId: string,
    firstName: string,
    lastName: string,
    email: string,
    companyName: string,
    title: string,
    role: string,
    phone: string,
    score: number,
    tagIds: number[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      firstName,
      lastName,
      email,
      companyName,
      title,
      role,
      phone,
      score,
      tagIds,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation createCandidate($input: CreateCandidateInput!) {
            createCandidate(input: $input) {
              candidateId
              error
            }
          }
        `,
        variables: { input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        console.log('Success');
        console.log(result);
        console.log(result['data'].createCandidate);
        if (successfunction) {
          successfunction(result['data'].createCandidate);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public createCompany(
    accountId: string,
    name: string,
    domain: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId: accountId,
      name: name,
      domain: domain,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation createCompany($input: CreateCompanyInput!) {
            createCompany(input: $input) {
              error
              companyId
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        console.log('Success');
        console.log(result);
        console.log(result['data'].createCompany);
        if (successfunction) {
          successfunction(result['data'].createCompany);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public createCandidateAttribute(
    accountId: string,
    name: string,
    icon: string,
    defaultValue: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId: accountId,
      name: name,
      icon: icon,
      defaultValue: defaultValue,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation createCandidateAttribute(
            $input: CreateCandidateAttributeInput!
          ) {
            createCandidateAttribute(input: $input) {
              error
              candidateAttributeId
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].createCandidateAttribute;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public createGroup(
    accountId: string,
    name: string,
    icon: string,
    sharing: number,
    rejectPreviouslyContacted: boolean,
    verifyEmails: boolean,
    rejectRiskyEmails: boolean,
    rejectUnknownEmails: boolean,
    rejectInvalidEmails: boolean,
    emailVerificationProvider: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      name,
      icon,
      sharing,
      rejectPreviouslyContacted,
      verifyEmails,
      rejectRiskyEmails,
      rejectUnknownEmails,
      rejectInvalidEmails,
      emailVerificationProvider,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation createGroup($input: CreateGroupInput!) {
            createGroup(input: $input) {
              error
              groupId
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].createGroup;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public deleteTag(
    accountId: string,
    tagId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    this.apollo
      .mutate({
        mutation: gql`
          mutation deleteCandidateTag($input: DeleteCandidateTagInput!) {
            deleteCandidateTag(input: $input) {
              error
            }
          }
        `,
        refetchQueries: [
          {
            query: candidateTagsQuery,
            variables: { accountId },
          },
        ],
        variables: {
          input: {
            accountId: accountId,
            tagId: tagId,
          },
        },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        if (result['data'].deleteCandidateTag['error']) {
          if (errorfunction) {
            console.warn(result['data'].deleteCandidateTag['error']);
            errorfunction(result['data'].deleteCandidateTag['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(result['data'].deleteCandidateTag);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public deleteAutoImport(
    accountId: string,
    autoImportId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    this.apollo
      .mutate({
        mutation: gql`
          mutation deleteAutoImport($input: DeleteAutoImportInput!) {
            deleteAutoImport(input: $input) {
              error
            }
          }
        `,
        variables: {
          input: {
            accountId,
            autoImportId,
          },
        },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].deleteAutoImport;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public setAutoImportPaused(
    accountId: string,
    autoImportId: string,
    paused: boolean,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    this.apollo
      .mutate({
        mutation: gql`
          mutation setAutoImportPaused($input: SetAutoImportPausedInput!) {
            setAutoImportPaused(input: $input) {
              error
            }
          }
        `,
        variables: {
          input: {
            accountId,
            autoImportId,
            paused,
          },
        },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        console.log(result);
        let data = result['data'].setAutoImportPaused;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public deleteGroup(
    accountId: string,
    groupId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      groupId,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation deleteGroup($input: DeleteGroupInput!) {
            deleteGroup(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        if (result['data'].deleteGroup['error']) {
          if (errorfunction) {
            console.warn(result['data'].deleteGroup['error']);
            errorfunction(result['data'].deleteGroup['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(result['data'].deleteGroup);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public deleteCandidateAttribute(
    accountId: string,
    candidateAttributeId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId: accountId,
      candidateAttributeId: candidateAttributeId,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation deleteCandidateAttribute(
            $input: DeleteCandidateAttributeInput!
          ) {
            deleteCandidateAttribute(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        if (result['data'].deleteCandidateAttribute['error']) {
          if (errorfunction) {
            console.warn(result['data'].deleteCandidateAttribute['error']);
            errorfunction(result['data'].deleteCandidateAttribute['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(result['data'].deleteCandidateAttribute);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public deleteFunnel(
    funnelId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId: funnelId,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation deleteFunnel($input: DeleteFunnelInput!) {
            deleteFunnel(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        if (result['data'].deleteFunnel['error']) {
          if (errorfunction) {
            console.warn(result['data'].deleteFunnel['error']);
            errorfunction(result['data'].deleteFunnel['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(result['data'].deleteFunnel);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public createCandidateTag(
    accountId: string,
    name: string,
    icon: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    this.apollo
      .mutate({
        mutation: gql`
          mutation createCandidateTag($input: CreateCandidateTagInput!) {
            createCandidateTag(input: $input) {
              candidateTagId
              error
            }
          }
        `,
        refetchQueries: [
          {
            query: candidateTagsQuery,
            variables: { accountId },
          },
        ],
        variables: {
          input: {
            accountId: accountId,
            name: name,
            icon: icon,
          },
        },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].createCandidateTag;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public setCandidateTagField({
    accountId,
    fieldName,
    value,
    tagId,
  }: {
    accountId: string;
    fieldName: string;
    value: string;
    tagId: string;
  }) {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation setCandidateTagField($input: SetCandidateTagFieldInput!) {
            setCandidateTagField(input: $input) {
              clientMutationId
              error
            }
          }
        `,
        update: (store, { data }) => {
          if (data['setCandidateTagField'].error) return;

          const newData = store.readQuery({
            query: candidateTagsQuery,
            variables: { accountId },
          });
          const newCandidateTags = newData['account']['candidateTags'].map(
            (candidateTag: ITag) => {
              return candidateTag.id === tagId
                ? { ...candidateTag, [fieldName]: value }
                : candidateTag;
            },
          );
          newData['account']['candidateTags'] = newCandidateTags;

          store.writeQuery({
            query: candidateTagsQuery,
            data: newData,
          });
        },
        variables: {
          input: { clientMutationId: accountId, fieldName, value, tagId },
        },
      })
      .pipe(
        map(({ data }) => {
          if (data['setCandidateTagField']['error']) {
            throw Error(data['setCandidateTagField']['error']);
          }
        }),
      );
  }

  // ------------------------------------------------------
  public setSettings(
    accountId: string,
    settingName: string,
    value: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      settingName,
      value,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation setAccountSetting($input: SetAccountSettingInput!) {
            setAccountSetting(input: $input) {
              status
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].setAccountSetting;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public setAircallUserId(
    accountId: string,
    logonId: string,
    aircallUserId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      logonId,
      aircallUserId,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation setAircallUserId($input: SetAircallUserIdInput!) {
            setAircallUserId(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].setAircallUserId;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public setAircallNumberId(
    accountId: string,
    logonId: string,
    aircallNumberId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      logonId,
      aircallNumberId,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation setAircallNumberId($input: SetAircallNumberIdInput!) {
            setAircallNumberId(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].setAircallNumberId;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public syncAircallTags(
    accountId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation syncAircallTags($input: SyncAircallTagsInput!) {
            syncAircallTags(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].syncAircallTags;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public removeAircallIntegration(
    accountId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation removeAircallIntegration(
            $input: RemoveAircallIntegrationInput!
          ) {
            removeAircallIntegration(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].removeAircallIntegration;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public candidateImportS3PresignedPostCreate(accountId: string) {
    return this.apollo
      .mutate({
        mutation: gql`
          mutation candidateImportS3PresignedPostCreate($accountId: ID!) {
            candidateImportS3PresignedPostCreate(
              input: { accountId: $accountId }
            ) {
              presignedPost {
                name
                value
              }
              url
              error
            }
          }
        `,
        variables: { accountId },
      })
      .pipe(
        map(({ data }) => {
          const error = data['candidateImportS3PresignedPostCreate']['error'];
          if (error) {
            throw Error(error);
          }
          return data['candidateImportS3PresignedPostCreate'];
        }),
      );
  }

  // ------------------------------------------------------
  public importCandidatesFromDrive(
    accountId: string,
    driveId: string,
    name: string,
    sheetId: string,
    mapping: {},
    funnelId,
    groupId,
    updateCandidates,
    monitorChanges,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      driveId,
      name,
      sheetId,
      funnelId,
      groupId,
      mapping: JSON.stringify(mapping).toString(),
      updateCandidates,
      monitorChanges,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation importCandidatesFromDrive(
            $input: ImportCandidatesFromDriveInput!
          ) {
            importCandidatesFromDrive(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        map(({ data }) => {
          if (data['importCandidatesFromDrive']['error']) {
            console.error(data['importCandidatesFromDrive']['error']);
            errorfunction(data['importCandidatesFromDrive']['error']);
            return null;
          }
          return { data };
        }),
      )
      .subscribe(result => {
        let data = result['data']['importCandidatesFromDrive'];
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public importCandidatesFromCsv(
    data: {
      accountId: string;
      objectKey: string;
      mapping: {};
      emailVerificationProvider?: string;
      updateCandidates?: boolean;
      funnelId?: string;
      groupId?: string;
    },
    errorfunction,
  ) {
    const input = {
      accountId: data.accountId,
      objectKey: data.objectKey,
      emailVerificationProvider: data.emailVerificationProvider || null,
      mapping: JSON.stringify(data.mapping).toString(),
      updateCandidates: data.updateCandidates,
      groupId: data.groupId,
      funnelId: data.funnelId,
    };

    return this.apollo
      .mutate({
        mutation: gql`
          mutation CandidateImportS3ObjectProcess(
            $input: CandidateImportS3ObjectProcessInput!
          ) {
            candidateImportS3ObjectProcess(input: $input) {
              error
            }
          }
        `,
        variables: { input },
      })
      .pipe(
        map(({ data }) => {
          if (data['candidateImportS3ObjectProcess']['error']) {
            console.error(data['candidateImportS3ObjectProcess']['error']);
            errorfunction(data['candidateImportS3ObjectProcess']['error']);
            return null;
          }
        }),
      );
  }

  // ------------------------------------------------------
  public createFunnel(
    accountId: string,
    name: string,
    sharing: number,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId: accountId,
      name: name,
      sharing: sharing,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation createFunnel($input: CreateFunnelInput!) {
            createFunnel(input: $input) {
              funnelId
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].createFunnel;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public clickToCall(
    accountId: string,
    phone: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      phone,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation clickToCall($input: ClickToCallInput!) {
            clickToCall(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].clickToCall;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public createOrUpdateTimeTable(
    accountId: string,
    timeTableId: string,
    name: string,
    timeZone: string,
    timeRanges: ITimeRange[],
    workingDays: boolean[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      timeTableId,
      name,
      timeZone,
      timeRanges,
      workingDays,
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation createOrEditTimeTable($input: CreateOrEditTimeTableInput!) {
            createOrEditTimeTable(input: $input) {
              timeTableId
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].createOrEditTimeTable;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public processSendQueue(
    inboxId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      inboxId: inboxId,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation processSendQueue($input: ProcessSendQueueInput!) {
            processSendQueue(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].processSendQueue;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public checkReplies(
    inboxId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      inboxId: inboxId,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation checkReplies($input: CheckRepliesInput!) {
            checkReplies(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].checkReplies;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public addCompaniesToFunnel(
    funnelId: string,
    companies: ICompany[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    //let candidateIds = candidates.map(a => a.id);

    let companyIds: string[] = [];

    companies.forEach(company => {
      companyIds.push(company.id);
    });

    const input = {
      funnelId: funnelId,
      companyIds: companyIds,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation addCompaniesToFunnel($input: AddCompaniesToFunnelInput!) {
            addCompaniesToFunnel(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].addCompaniesToFunnel;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public destroy(
    accountId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation deleteAccount($input: DeleteAccountInput!) {
            deleteAccount(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].deleteAccount;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public addCandidatesToFunnel(
    funnelId: string,
    candidateIds: string[],
    replyId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId,
      candidateIds,
      replyId,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation addCandidatesToFunnel($input: AddCandidatesToFunnelInput!) {
            addCandidatesToFunnel(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].addCandidatesToFunnel;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public assignCandidatesToGroup(
    groupId: string,
    candidateIds: string[],
    replyId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    //let candidateIds = candidates.map(a => a.id);
    const input = {
      groupId,
      candidateIds,
      replyId,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation assignCandidatesToGroup(
            $input: AssignCandidatesToGroupInput!
          ) {
            assignCandidatesToGroup(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].assignCandidatesToGroup;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public bulkCandidatesToFunnel(
    funnelId: string,
    searchFilter: any,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId: funnelId,
      searchFilter: JSON.stringify(searchFilter),
    };

    this.apollo
      .mutate({
        mutation: gql`
          mutation bulkCandidatesToFunnel(
            $input: BulkCandidatesToFunnelInput!
          ) {
            bulkCandidatesToFunnel(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].bulkCandidatesToFunnel;
        if (data['error']) {
          if (errorfunction) {
            console.error(data['error']);
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  bulkCancelCandidates(
    accountId: string,
    searchFilter: any,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      searchFilter: JSON.stringify(searchFilter),
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation bulkCancelCandidates($input: BulkCanceCandidatesInput!) {
            bulkCancelCandidates(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        const data = result['data'].bulkCancelCandidates;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  bulkExportCandidates(
    accountId: string,
    searchFilter: any,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      searchFilter: JSON.stringify(searchFilter),
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation bulkExportCandidates($input: BulkExportCandidatesInput!) {
            bulkExportCandidates(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        const data = result['data'].bulkExportCandidates;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  bulkExportDncDomains(
    accountId: string,
    searchFilter: any,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      searchFilter: JSON.stringify(searchFilter),
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation bulkExportDncDomains($input: BulkExportDncDomainsInput!) {
            bulkExportDncDomains(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        const data = result['data'].bulkExportDncDomains;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  bulkVerifyEmails(
    accountId: string,
    selectedAddon: string,
    searchFilter: any,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      selectedAddon,
      searchFilter: JSON.stringify(searchFilter),
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation bulkVerifyCandidates($input: BulkVerifCandidatesInput!) {
            bulkVerifyCandidates(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        const data = result['data'].bulkVerifyCandidates;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  bulkDeleteSchedules(
    accountId: string,
    searchFilter: any,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      searchFilter: JSON.stringify(searchFilter),
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation bulkDeleteSchedules($input: BulkDeleteSchedulesInput!) {
            bulkDeleteSchedules(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        const data = result['data'].bulkDeleteSchedules;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public deleteSchedules(
    accountId: string,
    scheduleIds: string[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      scheduleIds,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation deleteSchedules($input: DeleteSchedulesInput!) {
            deleteSchedules(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].deleteSchedules;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  bulkAssignCandidatesToGroup(
    accountId: string,
    groupId: string,
    searchFilter: any,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      groupId,
      searchFilter: JSON.stringify(searchFilter),
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation bulkAssignCandidatesToGroup(
            $input: BulkAssignCandidatesToGroupInput!
          ) {
            bulkAssignCandidatesToGroup(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        const data = result['data'].bulkAssignCandidatesToGroup;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public bulkDoNotContactCandidates(
    accountId: string,
    doNotContact: boolean,
    searchFilter: any,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      doNotContact,
      searchFilter: JSON.stringify(searchFilter),
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation bulkSetCandidatesAsDoNotContact(
            $input: BulkSetCandidatesAsDoNotContactInput!
          ) {
            bulkSetCandidatesAsDoNotContact(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        const data = result['data'].bulkSetCandidatesAsDoNotContact;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public bulkDeleteCandidates(
    accountId: string,
    searchFilter: any,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      searchFilter: JSON.stringify(searchFilter),
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation bulkDeleteCandidates($input: BulkDeletCandidatesInput!) {
            bulkDeleteCandidates(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        const data = result['data'].bulkDeleteCandidates;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public bulkDeleteCompanies(
    accountId: string,
    searchFilter: any,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      searchFilter: JSON.stringify(searchFilter),
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation bulkDeleteCompanies($input: BulkDeleteCompaniesInput!) {
            bulkDeleteCompanies(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        const data = result['data'].bulkDeleteCompanies;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public exportDncDomains(
    accountId: string,
    domainIds: string[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      domainIds,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation exportDncDomains($input: ExportDncDomainsInput!) {
            exportDncDomains(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].exportDncDomains;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public removeDncDomains(
    accountId: string,
    domainIds: string[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      domainIds,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation removeDncDomains($input: RemoveDncDomainsInput!) {
            removeDncDomains(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        let data = result['data'].removeDncDomains;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
        } else {
          if (successfunction) {
            successfunction(data);
          }
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public bulkRemoveDncDomains(
    accountId: string,
    searchFilter: any,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      searchFilter: JSON.stringify(searchFilter),
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation bulkRemoveDncDomains($input: BulkRemoveDncDomainsInput!) {
            bulkRemoveDncDomains(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        const data = result['data'].bulkRemoveDncDomains;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public bulkDeleteApplications(
    accountId: string,
    funnelId: string,
    searchFilter: any,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId,
      clientMutationId: accountId,
      searchFilter: JSON.stringify(searchFilter),
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation bulkDeleteApplications(
            $input: BulkDeleteApplicationsInput!
          ) {
            bulkDeleteApplications(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        const data = result['data'].bulkDeleteApplications;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public bulkCancelApplications(
    accountId: string,
    funnelId: string,
    searchFilter: any,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId,
      clientMutationId: accountId,
      searchFilter: JSON.stringify(searchFilter),
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation bulkCancelApplications(
            $input: BulkCancelApplicationsInput!
          ) {
            bulkCancelApplications(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        const data = result['data'].bulkCancelApplications;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public bulkResumeApplications(
    accountId: string,
    funnelId: string,
    searchFilter: any,
    processAt: number,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      funnelId,
      clientMutationId: accountId,
      searchFilter: JSON.stringify(searchFilter),
      processAt,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation bulkResumeApplications(
            $input: BulkResumeApplicationsInput!
          ) {
            bulkResumeApplications(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        const data = result['data'].bulkResumeApplications;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public generateInboxInvite(
    accountId: string,
    inboxId: string,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId: accountId,
      inboxId: inboxId,
    };

    return this.apollo
      .mutate({
        mutation: gql`
          mutation generateInboxInvite($input: GenerateInboxInviteInput!) {
            generateInboxInvite(input: $input) {
              secret
              url
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          console.log('DAT ERROR');
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      );
    /*
      .subscribe(result => {
        console.log('Success');
        console.log(result);
        console.log(result['data'].generateInboxInvite);
        if (successfunction) {
          successfunction(result['data'].generateInboxInvite);
        }
        return null;
      });
      */
  }

  // ------------------------------------------------------
  public addDncDomains(
    accountId: string,
    domains: string[],
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      domains,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation addDncDomains($input: AddDncDomainsInput!) {
            addDncDomains(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        const data = result['data'].addDncDomains;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public addCustomInbox(
    accountId: string,
    name,
    emailAddress,
    sendServer,
    sendPort,
    sendLogin,
    sendPassword,
    receiveServer,
    receivePort,
    receiveLogin,
    receivePassword,
    secret,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      name,
      accountId,
      emailAddress,
      sendServer,
      sendPort,
      sendLogin,
      sendPassword,
      receiveServer,
      receivePort,
      receiveLogin,
      receivePassword,
      secret,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation addCustomInbox($input: AddCustomInboxInput!) {
            addCustomInbox(input: $input) {
              inboxId
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        const data = result['data'].addCustomInbox;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public testCustomInboxSending(
    accountId: string,
    emailAddress,
    sendServer,
    sendPort,
    sendLogin,
    sendPassword,
    inboxId,
    secret,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      emailAddress,
      sendServer,
      sendPort,
      sendLogin,
      sendPassword,
      inboxId,
      secret,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation testCustomInboxSending(
            $input: TestCustomInboxSendingInput!
          ) {
            testCustomInboxSending(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        const data = result['data'].testCustomInboxSending;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }

  // ------------------------------------------------------
  public testCustomInboxReceiving(
    accountId: string,
    emailAddress,
    receiveServer,
    receivePort,
    receiveLogin,
    receivePassword,
    inboxId,
    secret,
    successfunction: SuccessCallback,
    errorfunction: ErrorCallback,
  ) {
    const input = {
      accountId,
      emailAddress,
      receiveServer,
      receivePort,
      receiveLogin,
      receivePassword,
      inboxId,
      secret,
    };
    this.apollo
      .mutate({
        mutation: gql`
          mutation testCustomInboxReceiving(
            $input: TestCustomInboxReceivingInput!
          ) {
            testCustomInboxReceiving(input: $input) {
              error
            }
          }
        `,
        variables: { input: input },
      })
      .pipe(
        catchError(err => {
          if (errorfunction) {
            errorfunction(err);
          }
          return null;
        }),
      )
      .subscribe(result => {
        const data = result['data'].testCustomInboxReceiving;
        if (data['error']) {
          if (errorfunction) {
            errorfunction(data['error']);
          }
          return null;
        }

        if (successfunction) {
          successfunction(data);
        }
        return null;
      });
    return null;
  }
}
